import { CircularProgress } from '@/components/common/CircularProgress';
import { validateStpFile } from '@/utils/validators';
import { Upload } from 'lucide-react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface ModelUploadZoneProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  uploadProgress?: number;
  currentStep?: string;
}

export const ModelUploadZone: FC<ModelUploadZoneProps> = ({
  onUpload,
  isUploading,
  uploadProgress = 0,
  currentStep = '',
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const stpFile = files.find((f) => f.name.toLowerCase().endsWith('.stp'));

    if (!stpFile) {
      toast.error(t('validation.stpFileRequired'));
      return;
    }

    const validation = validateStpFile(stpFile);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    await onUpload(stpFile);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateStpFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    await onUpload(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg ${
        isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary'
      } transition-colors cursor-pointer h-[200px] flex flex-col items-center justify-center gap-3`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
          fileInputRef.current?.click();
        }
      }}
      tabIndex={0}
      role="button"
    >
      {isUploading ? (
        <div className="w-full max-w-sm space-y-3">
          <div className="flex items-center justify-center">
            <CircularProgress progress={uploadProgress} size="medium" showPercentage={true} />
          </div>
          {currentStep ? (
            <p className="text-sm text-gray-900 text-center font-medium">{currentStep}</p>
          ) : (
            <p className="text-sm text-gray-600 text-center">{t('models.uploading')}</p>
          )}
        </div>
      ) : (
        <>
          <Upload size={32} className={isDragging ? 'text-primary' : 'text-gray-400'} />
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900">
              {isDragging ? t('projects.dropHere') : t('models.uploadModel')}
            </p>
            <p className="text-xs text-gray-500">{t('projects.dragDrop')}</p>
          </div>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".stp"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isUploading}
      />
    </div>
  );
};
